import React, { useContext, useEffect, useState, useCallback } from 'react';
import { MdTrendingUp, MdTrendingDown, MdSavings, MdShield, MdWarningAmber,
  MdCheckCircle, MdRadioButtonUnchecked, MdAutoAwesome, MdArrowForward, MdArrowBack } from 'react-icons/md';
import { AppContext } from '../contexts/AppContext';
import { useProfile } from '../contexts/ProfileContext';
import { fetchCashflow, getMemory, decideAssumption, previewAction, executeAction } from '../services/backendAPI';

// Hold the last result so reopening the screen shows it instantly while a fresh
// fetch happens in the background. Keyed on sessionId + customerId: the sessionId
// is REUSED when you switch profiles, so it alone can't tell profiles apart — the
// customerId (from sessionSummary, refreshed after each profile load) does.
let cachedCashflow = { key: null, data: null };

const STAGE_META = {
  security:    { label: 'Deficit',    short: 'Deficit',    color: '#d9822b', Icon: MdShield,
                 blurb: 'Building Security — cover essentials, clear high-interest debt, start an emergency fund.' },
  stability:   { label: 'Breakeven',  short: 'Breakeven',  color: '#1976d2', Icon: MdSavings,
                 blurb: 'Finding Stability — essentials covered; now grow automated savings and steady habits.' },
  fulfillment: { label: 'Surplus',    short: 'Surplus',    color: '#008a0d', Icon: MdTrendingUp,
                 blurb: 'Room for Fulfillment — security and stability in place; conscious spending is appropriate.' },
};

// The wellbeing stages form a ladder — you climb in this order.
const STAGE_ORDER = ['security', 'stability', 'fulfillment'];

// Customer-facing names for the spend tiers. The backend keys (T1/T2/T3) are
// internal shorthand; users see plain language here. Falls back to the backend
// label, then the raw key.
const TIER_UI = {
  T1_fixed_essential:    'Essentials · Fixed',
  pay_yourself_first:    'Savings (Pay Yourself First)',
  T2_variable_essential: 'Essentials · Variable',
  T3_discretionary:      'Lifestyle · Discretionary',
};

const money = (n) => `$${Math.round(Number(n) || 0).toLocaleString()}`;
const pct   = (x) => `${Math.round((Number(x) || 0) * 100)}%`;

// The single cashflow box — the showcase. As the user chats with IVA this grows
// into a fuller Finances picture; for now it's one focused card.
function CashflowBox({ data, onChatHandoff, onPayDownCard }) {
  const cf = data.cashflow || {};
  const a  = data.assessment || {};
  const stage = STAGE_META[data.stage] || STAGE_META.security;
  // Whether the full 3-stage explainer is expanded (collapsed by default).
  const [showStages, setShowStages] = useState(false);

  // What's needed to climb to the next stage, and a one-tap handoff that asks
  // IVA to start working through it.
  const next = data.nextStage;
  const nextMeta = next ? STAGE_META[next.target] : null;
  const startNextStageAgent = () => {
    if (!onChatHandoff || !next) return;
    const unmet = (next.requirements || []).filter(r => !r.met).map(r => r.label);
    const todo = unmet.length ? unmet.join('; ') : (next.requirements || []).map(r => r.label).join('; ');
    onChatHandoff(
      `I'm at the "${stage.label}" stage on my cashflow and I want to reach "${nextMeta?.label || next.target}". ` +
      `What I still need to do: ${todo}. Walk me through the first step and, where you can, start the process for me.`,
    );
  };
  const surplus = cf.surplus ?? (cf.income - cf.consumption);
  const positive = surplus >= 0;
  const labels = data.tierLabels || {};

  const tierRows = Object.entries(a.tierTotals || {}).map(([tier, amount]) => ({
    tier,
    label: TIER_UI[tier] || labels[tier] || tier,
    amount,
    pct: a.tierPct?.[tier] ?? 0,
  }));

  return (
    <div className="ins-card">
      {/* Stage banner — with an ⓘ toggle that reveals all 3 stages on demand */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px',
        borderRadius: 12, background: `${stage.color}14`, marginBottom: showStages ? 8 : 14,
      }}>
        <div style={{
          width: 36, height: 36, borderRadius: 10, flexShrink: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: `${stage.color}22`, color: stage.color,
        }}>
          <stage.Icon size={20} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 800, color: 'var(--text-dark)', fontSize: 15 }}>{stage.label}</div>
          <div style={{ fontSize: 12, color: 'var(--text-medium)' }}>{stage.blurb}</div>
        </div>
        <button
          type="button"
          onClick={() => setShowStages(v => !v)}
          aria-pressed={showStages}
          aria-label="What do the stages mean?"
          title="What do the stages mean?"
          style={{
            width: 24, height: 24, flexShrink: 0, borderRadius: '50%', cursor: 'pointer',
            border: `1.5px solid ${stage.color}`, background: showStages ? stage.color : 'transparent',
            color: showStages ? '#fff' : stage.color,
            font: '700 13px/1 Georgia, serif', fontStyle: 'italic',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >i</button>
      </div>

      {/* Stage explainer — collapsed by default; the ⓘ reveals the full
          security → stability → fulfillment flow, left to right. */}
      {showStages && (
        <div style={{ marginBottom: 14, display: 'flex', alignItems: 'stretch', gap: 4 }}>
          {STAGE_ORDER.map((key, i) => {
            const m = STAGE_META[key];
            const isCurrent = key === data.stage;
            return (
              <React.Fragment key={key}>
                <div style={{
                  flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center',
                  gap: 4, padding: '8px 6px', borderRadius: 10,
                  background: isCurrent ? `${m.color}14` : 'transparent',
                  border: `1px solid ${isCurrent ? m.color : 'var(--border-color, #e5e7eb)'}`,
                }}>
                  <div style={{
                    width: 28, height: 28, borderRadius: 8,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    background: `${m.color}22`, color: m.color,
                  }}>
                    <m.Icon size={16} />
                  </div>
                  <div style={{ fontSize: 12, fontWeight: 700, color: m.color }}>{m.short}</div>
                  {isCurrent && (
                    <div style={{ fontSize: 9, fontWeight: 700, color: m.color, letterSpacing: 0.3 }}>YOU'RE HERE</div>
                  )}
                  <div style={{ fontSize: 10.5, color: 'var(--text-medium)', lineHeight: 1.3 }}>{m.blurb}</div>
                </div>
                {i < STAGE_ORDER.length - 1 && (
                  <div style={{
                    display: 'flex', alignItems: 'center', flexShrink: 0,
                    color: 'var(--text-light)', fontSize: 18, fontWeight: 700,
                  }}>›</div>
                )}
              </React.Fragment>
            );
          })}
        </div>
      )}

      {/* Headline: monthly surplus / deficit */}
      <div style={{ textAlign: 'center', marginBottom: 14 }}>
        <div style={{ fontSize: 12, color: 'var(--text-light)', textTransform: 'uppercase', letterSpacing: 0.5 }}>
          Monthly {positive ? 'surplus' : 'shortfall'}
        </div>
        <div style={{
          fontSize: 34, fontWeight: 800, lineHeight: 1.1,
          color: positive ? '#008a0d' : 'var(--td-red)',
          display: 'inline-flex', alignItems: 'center', gap: 6,
        }}>
          {positive ? <MdTrendingUp size={26} /> : <MdTrendingDown size={26} />}
          {money(Math.abs(surplus))}
        </div>
        <div style={{ fontSize: 12, color: 'var(--text-light)', marginTop: 2 }}>
          {positive ? `≈ ${money(cf.annualSurplus || surplus * 12)} a year` : 'spending more than you bring in'}
        </div>
      </div>

      {/* Income vs. spend */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 14 }}>
        <Stat label="Income / mo" value={money(cf.income)} />
        <Stat label="Spending / mo" value={money(cf.consumption)} />
        <Stat label="Saving / mo" value={money(cf.saving)} />
      </div>

      {/* Health markers */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 14 }}>
        <Stat label="Savings rate" value={pct(a.effectiveSavingsRate ?? a.savingsRate)} />
        <Stat label="Emergency fund"
              value={a.emergencyFundMonths != null ? `${a.emergencyFundMonths} mo` : '—'} />
        <Stat label="High-int. debt" value={a.highInterestDebt ? 'Yes' : 'No'}
              accent={a.highInterestDebt ? 'var(--td-red)' : '#008a0d'} />
      </div>

      {/* Action: pay down the card — moves real balances + re-rolls the cashflow */}
      {a.highInterestDebt && onPayDownCard && (
        <button type="button" onClick={onPayDownCard}
          style={{
            width: '100%', marginBottom: 14, display: 'inline-flex', alignItems: 'center',
            justifyContent: 'center', gap: 8, background: 'var(--td-red)', color: '#fff', border: 'none',
            borderRadius: 10, padding: '10px 14px', fontWeight: 700, fontSize: 13, cursor: 'pointer',
          }}>
          <MdAutoAwesome size={16} /> Pay down the card
        </button>
      )}

      {/* Reach the next stage — unmet exit gates + a one-tap agent handoff */}
      {next && nextMeta && (
        <div style={{
          marginBottom: 14, padding: 12, borderRadius: 12,
          border: `1px solid ${nextMeta.color}40`, background: `${nextMeta.color}0d`,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
            <nextMeta.Icon size={16} style={{ color: nextMeta.color }} />
            <span style={{ fontSize: 13, fontWeight: 800, color: 'var(--text-dark)' }}>
              To reach {nextMeta.label}
            </span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 12 }}>
            {(next.requirements || []).map((r, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 13 }}>
                {r.met
                  ? <MdCheckCircle size={16} style={{ color: '#008a0d', flexShrink: 0, marginTop: 1 }} />
                  : <MdRadioButtonUnchecked size={16} style={{ color: 'var(--text-light)', flexShrink: 0, marginTop: 1 }} />}
                <span style={{ color: r.met ? 'var(--text-light)' : 'var(--text-dark)',
                               textDecoration: r.met ? 'line-through' : 'none' }}>
                  {r.label}
                  {r.detail && <span style={{ color: 'var(--text-light)' }}> ({r.detail})</span>}
                </span>
              </div>
            ))}
          </div>
          {onChatHandoff && (
            <button
              type="button"
              onClick={startNextStageAgent}
              style={{
                width: '100%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                background: nextMeta.color, color: '#fff', border: 'none', borderRadius: 10,
                padding: '10px 14px', fontWeight: 700, fontSize: 13, cursor: 'pointer',
              }}
            >
              <MdAutoAwesome size={16} /> Help me get there <MdArrowForward size={16} />
            </button>
          )}
        </div>
      )}

      {/* Where the money goes — tier mix */}
      {tierRows.length > 0 && (
        <div style={{ marginBottom: tierRows.length ? 4 : 0 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-light)', marginBottom: 6 }}>
            Where your money goes
          </div>
          {tierRows.map(r => (
            <div key={r.tier} style={{ marginBottom: 8 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 3 }}>
                <span>{r.label}</span>
                <span style={{ color: 'var(--text-light)' }}>{money(r.amount)} · {pct(r.pct)}</span>
              </div>
              <div style={{ height: 6, borderRadius: 3, background: 'var(--border, #e5e7eb)' }}>
                <div style={{
                  height: '100%', borderRadius: 3, width: `${Math.min(100, (r.pct || 0) * 100)}%`,
                  background: stage.color,
                }} />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Benchmark flags */}
      {Array.isArray(a.benchmarkFlags) && a.benchmarkFlags.length > 0 && (
        <div style={{ marginTop: 10, padding: '8px 10px', borderRadius: 10, background: '#fff7ed' }}>
          {a.benchmarkFlags.map((f, i) => (
            <div key={i} style={{ fontSize: 12, color: '#9a3412', display: 'flex', gap: 6, alignItems: 'flex-start' }}>
              <MdWarningAmber size={14} style={{ flexShrink: 0, marginTop: 1 }} /> {f}
            </div>
          ))}
        </div>
      )}

      {/* Next steps from the gate */}
      {Array.isArray(data.nextSteps) && data.nextSteps.length > 0 && (
        <div style={{ marginTop: 12 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-light)', marginBottom: 4 }}>
            Suggested next steps
          </div>
          <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13 }}>
            {data.nextSteps.slice(0, 4).map((s, i) => <li key={i} style={{ marginBottom: 2 }}>{s}</li>)}
          </ul>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div style={{
      flex: 1, textAlign: 'center', padding: '8px 4px',
      borderRadius: 10, background: 'var(--bg-light-gray, #f7f8fa)',
    }}>
      <div style={{ fontSize: 16, fontWeight: 800, color: accent || 'var(--text-dark)' }}>{value}</div>
      <div style={{ fontSize: 11, color: 'var(--text-light)' }}>{label}</div>
    </div>
  );
}

// Confirmations for the guesses IVA derived about this customer. Lives here in
// Finances (below the cashflow items) so the customer can verify the assumptions
// that shape their financial picture. Confirm → promoted to a stated fact;
// "Not quite" → retired so it never resurfaces. Reuses the memory assumption flow.
// Small pill button used by the assumption cards.
function PillButton({ onClick, disabled, primary, children }) {
  return (
    <button type="button" disabled={disabled} onClick={onClick}
      style={{
        borderRadius: 8, padding: '6px 12px', fontWeight: 700, fontSize: 12, cursor: 'pointer',
        whiteSpace: 'nowrap', opacity: disabled ? 0.5 : 1,
        ...(primary
          ? { border: 'none', background: '#008a0d', color: '#fff' }
          : { background: 'transparent', color: 'var(--text-medium)', border: '1px solid var(--border-color, #e5e7eb)' }),
      }}>
      {children}
    </button>
  );
}

function AssumptionsBox({ customerId, sessionId, memoryVersion, onChatHandoff }) {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(null); // id currently being decided
  const [step, setStep] = useState({});   // id -> 'cancel' once a duplicate sub is confirmed a dup

  useEffect(() => {
    if (!customerId) { setItems([]); return; }
    let cancelled = false;
    (async () => {
      const mem = await getMemory(customerId);
      if (cancelled) return;
      const list = (mem?.understanding?.assumptions || []).filter(a => a.status === 'unconfirmed');
      setItems(list);
    })();
    return () => { cancelled = true; };
  }, [customerId, sessionId, memoryVersion]); // refetch when memory is cleared

  const decide = async (id, decision) => {
    setBusy(id);
    const res = await decideAssumption(customerId, id, decision);
    if (res?.ok || res === null) setItems(prev => prev.filter(a => a.id !== id));
    setBusy(null);
  };

  // Duplicate-subscription cards get a two-step flow: is it a duplicate? → cancel it?
  const isDupSub = (a) => typeof a.id === 'string' && a.id.startsWith('duplicate_sub_');
  const brandOf = (a) => a.id.replace('duplicate_sub_', '').replace(/_/g, ' ').trim() || 'subscription';
  const startCancel = (a) => {
    // Hand off to IVA chat to actually cancel — placeholder until the action
    // system can do it directly. Either way the guess is resolved.
    if (onChatHandoff) onChatHandoff(`Please help me cancel the duplicate ${brandOf(a)} subscription — stop the extra one and confirm what changes.`);
    decide(a.id, 'dismiss');
  };

  if (!items.length) return null;

  return (
    <div className="ins-card" style={{ marginTop: 10 }}>
      <div style={{ fontSize: 13, fontWeight: 800, color: 'var(--text-dark)', marginBottom: 2 }}>
        Help IVA understand you
      </div>
      <div style={{ fontSize: 12, color: 'var(--text-light)', marginBottom: 10 }}>
        A few things IVA assumed from your accounts — confirm so your picture stays accurate.
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {items.map(a => {
          const dup = isDupSub(a);
          const atCancelStep = step[a.id] === 'cancel';
          return (
            <div key={a.id} style={{
              display: 'flex', alignItems: 'flex-start', gap: 10,
              padding: '10px', borderRadius: 10, background: 'var(--bg-light-gray, #f7f8fa)',
            }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, color: 'var(--text-dark)' }}>
                  {dup && atCancelStep
                    ? `Cancel the extra ${brandOf(a)} subscription?`
                    : a.text}
                </div>
                {a.basis && !atCancelStep && (
                  <div style={{ fontSize: 11, color: 'var(--text-light)', marginTop: 2 }}>{a.basis}</div>
                )}
              </div>
              <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                {dup ? (
                  atCancelStep ? (
                    <>
                      <PillButton primary disabled={busy === a.id} onClick={() => startCancel(a)}>Cancel it</PillButton>
                      <PillButton disabled={busy === a.id} onClick={() => decide(a.id, 'dismiss')}>Keep both</PillButton>
                    </>
                  ) : (
                    <>
                      <PillButton primary disabled={busy === a.id} onClick={() => setStep(s => ({ ...s, [a.id]: 'cancel' }))}>It's a duplicate</PillButton>
                      <PillButton disabled={busy === a.id} onClick={() => decide(a.id, 'dismiss')}>It's for family</PillButton>
                    </>
                  )
                ) : (
                  <>
                    <PillButton primary disabled={busy === a.id} onClick={() => decide(a.id, 'confirm')}>Yes</PillButton>
                    <PillButton disabled={busy === a.id} onClick={() => decide(a.id, 'dismiss')}>Not quite</PillButton>
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// A plain-English recap of what IVA has distilled about the customer's money,
// shown so they can sanity-check it and correct anything that's wrong. The
// summary text comes straight from the understanding engine (persona.summary);
// "Something's off" hands off to chat, whose extraction pipeline merges the
// correction into aboutMe — no dedicated write endpoint needed.
export function UnderstandingSummaryCard({ customerId, memoryVersion, onChatHandoff }) {
  const [persona, setPersona] = useState(null);
  const [confirmed, setConfirmed] = useState(false);

  useEffect(() => {
    if (!customerId) { setPersona(null); return; }
    let cancelled = false;
    (async () => {
      const mem = await getMemory(customerId);
      if (cancelled) return;
      setPersona(mem?.understanding?.persona || null);
      setConfirmed(false);
    })();
    return () => { cancelled = true; };
  }, [customerId, memoryVersion]);

  if (!customerId || !persona?.summary) return null;

  const money = (v) => (typeof v === 'number' ? `$${Math.round(v).toLocaleString()}` : null);
  const facts = [
    persona.monthlyIncomeEst != null && `${money(persona.monthlyIncomeEst)}/mo coming in`,
    persona.monthlySpendEst != null && `${money(persona.monthlySpendEst)}/mo going out`,
    persona.monthlySavingsCapacity != null && `about ${money(persona.monthlySavingsCapacity)}/mo you could save`,
  ].filter(Boolean);

  return (
    <div className="ins-card" style={{ marginTop: 10 }}>
      <div style={{ fontSize: 13, fontWeight: 800, color: 'var(--text-dark)', marginBottom: 2 }}>
        Here's what I understand about you
      </div>
      <div style={{ fontSize: 13, color: 'var(--text-medium)', lineHeight: 1.5, marginBottom: facts.length ? 6 : 10 }}>
        {persona.summary}
      </div>
      {facts.length > 0 && (
        <div style={{ fontSize: 12, color: 'var(--text-light)', marginBottom: 10 }}>
          In short: {facts.join(', ')}.
        </div>
      )}
      {confirmed ? (
        <div style={{ fontSize: 12, color: '#008a0d', fontWeight: 700 }}>
          Thanks — glad I've got the picture right ✓
        </div>
      ) : (
        <div style={{ display: 'flex', gap: 6 }}>
          <PillButton primary onClick={() => setConfirmed(true)}>That's right ✓</PillButton>
          <PillButton onClick={() => onChatHandoff && onChatHandoff(
            'Some of what you understand about my finances is off — let me correct it: '
          )}>Something's off</PillButton>
        </div>
      )}
    </div>
  );
}

const FinancesScreen = ({ onNavigate, onBack, onChatHandoff }) => {
  const { sessionId, sessionSummary, memoryVersion, simDate } = useContext(AppContext);
  const { loadedProfileData } = useProfile();

  // A key that changes when the active PROFILE changes — sessionId is reused
  // across profile switches, so we fold in the customerId to detect a swap.
  const customerId = sessionSummary?.customerId || null;
  const cacheKey = sessionId ? `${sessionId}:${customerId || ''}` : null;

  // Bypass the module-level cache when sim is active — each simDate is a
  // different projection and must not overwrite or serve the live result.
  const [data, setData] = useState(
    !simDate && cachedCashflow.key === cacheKey ? cachedCashflow.data : null
  );
  const [loading, setLoading] = useState(false);

  const store = useCallback((d, key) => {
    if (!simDate) cachedCashflow = { key, data: d };
    setData(d);
  }, [simDate]);

  useEffect(() => {
    if (!sessionId) return;
    // Serve cached data instantly when not in sim mode. In sim mode, keep
    // whatever is already displayed so there's no blank flash between ticks.
    if (!simDate && cachedCashflow.key === cacheKey) setData(cachedCashflow.data);
    let cancelled = false;
    setLoading(true);
    (async () => {
      const d = await fetchCashflow(sessionId, simDate || null);
      if (!cancelled && d?.ready) store(d, cacheKey);
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [sessionId, cacheKey, store, simDate]);

  // Re-pull the cashflow after an action mutates the working copy (the server
  // invalidates its cache, so this recomputes balances + stage).
  const refreshCashflow = useCallback(async () => {
    if (!sessionId) return;
    const d = await fetchCashflow(sessionId);
    if (d?.ready) store(d, cacheKey);
  }, [sessionId, cacheKey, store]);

  // Confirm-gated pay-down: preview → confirm the amount → execute → refresh.
  const handlePayDownCard = useCallback(async () => {
    if (!sessionId) return;
    const pv = await previewAction(sessionId, 'pay_down_credit_card', {});
    if (!pv?.ok) { window.alert(pv?.reason || "Can't pay down right now."); return; }
    const raw = window.prompt(`${pv.summary}.\n\nEnter an amount to pay (max $${pv.amount.toLocaleString()}):`, String(pv.amount));
    if (raw == null) return;
    const amount = Number(raw);
    if (!(amount > 0)) return;
    const res = await executeAction(sessionId, 'pay_down_credit_card', { amount });
    if (res?.ok) await refreshCashflow();
    else window.alert(res?.reason || 'Payment could not be completed.');
  }, [sessionId, refreshCashflow]);

  const profileName = loadedProfileData?.name;

  return (
    <div className="screen insights-screen bh-flow">
      <header className="bh-flow-hero">
        <div className="bh-flow-hero-left">
          <button className="bh-flow-back" onClick={onBack || (() => onNavigate && onNavigate('insightsScreen'))} aria-label="Back">
            <MdArrowBack />
          </button>
          <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
            <h1 className="bh-flow-title">Finances</h1>
            {profileName && (
              <div style={{ fontSize: 12, color: 'rgba(0,0,0,0.6)', fontWeight: 500, marginTop: 2 }}>{profileName}</div>
            )}
          </div>
        </div>
      </header>

      <div className="scroll-content gray-bg ins-scroll">
        {simDate && (
          <div style={{
            fontSize: 9, fontFamily: 'monospace', color: '#f59e0b', letterSpacing: 0.8,
            background: '#f59e0b14', border: '1px solid #f59e0b44',
            borderRadius: 6, padding: '4px 10px', textAlign: 'center', marginBottom: 6,
          }}>
            SIM {simDate}
          </div>
        )}
        <div style={{ fontSize: 12, color: 'var(--text-light)', textAlign: 'center', marginBottom: 2 }}>
          your cashflow at a glance
        </div>

        {!sessionId && (
          <div className="ins-card" style={{ textAlign: 'center', color: 'var(--text-light)', fontSize: 13 }}>
            Open a profile to see your financial health.
          </div>
        )}

        {sessionId && !data && loading && (
          <div className="ins-card" style={{ textAlign: 'center', color: 'var(--text-light)', fontSize: 13 }}>
            IVA is reading your cashflow…
          </div>
        )}

        {sessionId && !data && !loading && (
          <div className="ins-card" style={{ textAlign: 'center', color: 'var(--text-light)', fontSize: 13 }}>
            Cashflow isn't available for this profile yet.
          </div>
        )}

        {data && <CashflowBox data={data} onChatHandoff={onChatHandoff} onPayDownCard={handlePayDownCard} />}

        {data && <UnderstandingSummaryCard customerId={customerId} memoryVersion={memoryVersion} onChatHandoff={onChatHandoff} />}

        {data && <AssumptionsBox customerId={customerId} sessionId={sessionId} memoryVersion={memoryVersion} onChatHandoff={onChatHandoff} />}

        {data && (
          <div style={{ fontSize: 12, color: 'var(--text-light)', textAlign: 'center', margin: '8px 12px 0' }}>
            This is a starting picture. Chat with IVA about your money and it will
            build out a fuller view of your finances here.
          </div>
        )}
      </div>
    </div>
  );
};

export default FinancesScreen;
